import { useState, useMemo, useEffect } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import {
  myTasksLocationsListName,
  myTasksDepartmentsListName,
  myTasksRegionsListName,
  myTasksKPIListName,
  myTasks5WayStatusesListName,
  myTasksTriggersListName,
  myTasksDeviationReportTriggersListName,
  myTasksDeviationReportDependListName,
  myTasksDeviationReportKpiByTriggerListName,
  myTasks5WayDependListName,
  myTasksKpiByTriggerListName,
} from "src/api/config/myTasksLists.config";
import TGDatePicker from "src/components/ui/TGDatePicker";
import { TGDisclosure } from "src/components/ui/TGDisclosure";
import { TGRadioList } from "src/components/ui/TGRadioList";
import { useAppDispatch } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import { getItems } from "src/modules/spItems/actions";
import { TGListWithSearch } from "src/pages/DashboardPage/acadia/components/Filters/ListsSearch";
import { CheckBoxListItem, RadioBoxListItem, UserListItem } from "src/types/main";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { SubResetFilter } from "../../meetingsPage/components/MeetingCard";
import { FilterUser } from "../ActionsReport/ActionsReport";
import { SubmitFiveWhyReportFiltersType } from "./types";
import { TGCheckList } from "src/components/ui/TGCheckList";
import { getItemsRequest } from "src/modules/spItems/api";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { chunkArray } from "src/utils/array.utils";

function MatrixReportFilters({
  submitFilters,
  changeSubmitFilters,
  resetSubmitFilters,
}: {
  submitFilters: SubmitFiveWhyReportFiltersType;
  changeSubmitFilters: (e: SubmitFiveWhyReportFiltersType) => void;
  resetSubmitFilters: () => void;
}) {
  const {
    locations,
    departments,
    sectors,
    actionsPlanned,
    author,
    createDate,
    isReactPlanAccept,
    isRootProblemIdentify,
    kpi,
    repeatProblem,
    deviationParticipants,
    deviationStatuses,
    deviationTriggers,
    fiveWhyResponsible,
    fiveWhyStatuses,
    fiveWhyTriggers,
  } = submitFilters;

  const { data: locationsResponseData, status: locationsStatus } = useGetSelect(myTasksLocationsListName);
  const { data: departmentsResponseData, status: departmentsStatus } = useGetSelect(myTasksDepartmentsListName);
  const { data: sectorsResponseData, status: sectorsStatus } = useGetSelect(myTasksRegionsListName);
  const { data: fiveWayStatusesResponseData, status: fiveWayStatusesStatus } =
    useGetSelect(myTasks5WayStatusesListName);
  const { data: kpiResponseData, status: kpiStatus } = useGetSelect(myTasksKPIListName);
  const { data: triggersResponseData, status: triggersStatus } = useGetSelect(myTasksTriggersListName);
  const { data: deviationTriggersResponseData, status: deviationTriggersStatus } = useGetSelect(
    myTasksDeviationReportTriggersListName
  );

  const [deviationLoading, setDeviationLoading] = useState(false);
  const [fiveWhyLoading, setFiveWhyLoading] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);

  const [locationOptions, setLocationOptions] = useState<CheckBoxListItem[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<CheckBoxListItem[]>([]);
  const [sectorOptions, setSectorOptions] = useState<CheckBoxListItem[]>([]);

  const [triggersOptions, setTriggersOptions] = useState<CheckBoxListItem[]>([]);
  const [deviationTriggersOptions, setDeviationTriggersOptions] = useState<CheckBoxListItem[]>([]);
  const [kpiOptions, setKpiOptions] = useState<CheckBoxListItem[]>([]);

  const [filterLoading, setFilterLoading] = useState(false);

  const dispatch = useAppDispatch();

  const [firstSelectedFilter, setFirstSelectedFilter] = useState<
    "locations" | "departments" | "sectors" | "fiveWhyTriggers" | "deviationTriggers" | "kpi" | null
  >(null);

  function checkListItem(key: keyof SubmitFiveWhyReportFiltersType, value: CheckBoxListItem) {
    const values = submitFilters[key] as CheckBoxListItem[];

    let newValues = [...values];
    if (newValues.length > 0 && newValues.find((f) => f.id === value.id)) {
      const filtered = values.filter((n) => n.id !== value.id);

      newValues = filtered;
    } else {
      newValues.push(value);
    }

    changeSubmitFilters({ ...submitFilters, [key]: newValues });
  }

  useEffect(() => {
    const mainFilters = {
      locations: submitFilters.locations,
      departments: submitFilters.departments,
      sectors: submitFilters.sectors,
      fiveWhyTriggers: submitFilters.fiveWhyTriggers,
      deviationTriggers: submitFilters.deviationTriggers,
      kpi: submitFilters.kpi,
    };

    const selectedFilters = Object.entries(mainFilters).filter(([key, value]) => value.length > 0);

    if (selectedFilters.length === 0) {
      return resetFilters();
    }

    setKpiLoading(true);
    setFiveWhyLoading(true);
    setDeviationLoading(true);
    setFilterLoading(true);

    const key = selectedFilters[0][0] as keyof typeof mainFilters;

    if (selectedFilters.length === 1 && firstSelectedFilter === null) {
      return setFirstSelectedFilter(key);
    } else if (selectedFilters.length === 1 && key !== firstSelectedFilter && firstSelectedFilter !== null) {
      return setFirstSelectedFilter(key);
    }

    setKpiLoading(false);
    setFiveWhyLoading(false);
    setDeviationLoading(false);
    setFilterLoading(false);
  }, [
    submitFilters.fiveWhyTriggers.length,
    submitFilters.deviationTriggers.length,
    submitFilters.kpi.length,
    submitFilters.locations.length,
    submitFilters.departments.length,
    submitFilters.sectors.length,
  ]);

  const resetFilters = () => {
    setFirstSelectedFilter(null);
    setLocationOptions(locationOptions.map((f) => ({ ...f, disabled: false })));
    setDepartmentOptions(departmentOptions.map((f) => ({ ...f, disabled: false })));
    setSectorOptions(sectorOptions.map((f) => ({ ...f, disabled: false })));
    setKpiOptions(kpiOptions.map((f) => ({ ...f, disabled: false })));
    setTriggersOptions(triggersOptions.map((f) => ({ ...f, disabled: false })));
    setDeviationTriggersOptions(deviationTriggersOptions.map((f) => ({ ...f, disabled: false })));
  };

  function radioListItem(key: keyof SubmitFiveWhyReportFiltersType, value: RadioBoxListItem) {
    changeSubmitFilters({ ...submitFilters, [key]: value });
  }

  function checkListDateItem(key: keyof SubmitFiveWhyReportFiltersType, value: DateValueType) {
    changeSubmitFilters({ ...submitFilters, [key]: value });
  }

  function checkListUserItem(key: keyof SubmitFiveWhyReportFiltersType, value: UserListItem) {
    const values = submitFilters[key] as UserListItem[];

    let newValue = [...values];
    if (newValue.length > 0 && newValue.find((f) => f.id === value.id)) {
      const filtered = values.filter((n: any) => n.id !== value.id);
      newValue = filtered;
    } else {
      newValue.push(value);
    }
    changeSubmitFilters({ ...submitFilters, [key]: newValue });
  }

  // Чистит всё
  function clearAll() {
    resetSubmitFilters();
    resetFilters();
  }

  // Функция для определения следующего активного фильтра в иерархии
  const getNextActiveFilter = (filters: SubmitFiveWhyReportFiltersType) => {
    // Проверяем наличие значений в порядке приоритета
    if (filters.locations.length > 0) return "locations";
    if (filters.departments.length > 0) return "departments";
    if (filters.sectors.length > 0) return "sectors";
    if (filters.fiveWhyTriggers.length > 0) return "fiveWhyTriggers";
    if (filters.deviationTriggers.length > 0) return "deviationTriggers";
    if (filters.kpi.length > 0) return "kpi";
    return null;
  };

  function reset(key: keyof typeof submitFilters, value: any) {
    changeSubmitFilters({ ...submitFilters, [key]: value });

    // Если сбрасываем текущий первый фильтр
    if (
      (key === "locations" && firstSelectedFilter === "locations") ||
      (key === "departments" && firstSelectedFilter === "departments") ||
      (key === "sectors" && firstSelectedFilter === "sectors") ||
      (key === "fiveWhyTriggers" && firstSelectedFilter === "fiveWhyTriggers") ||
      (key === "deviationTriggers" && firstSelectedFilter === "deviationTriggers") ||
      (key === "kpi" && firstSelectedFilter === "kpi")
    ) {
      // Ищем следующий активный фильтр в иерархии
      const nextFilter = getNextActiveFilter(submitFilters);
      setFirstSelectedFilter(nextFilter);
    }
  }

  const isFiltersEmpty = useMemo(() => {
    const isEmpty =
      !!locations.length ||
      !!departments.length ||
      !!sectors.length ||
      actionsPlanned.id !== 0 ||
      !!author.length ||
      !!createDate?.startDate ||
      !!createDate?.endDate ||
      isReactPlanAccept.id !== 0 ||
      isRootProblemIdentify.id !== 0 ||
      !!kpi.length ||
      repeatProblem.id !== 0;

    return isEmpty;
  }, [submitFilters]);

  useEffect(() => {
    // Lists
    dispatch(getItems({ listName: myTasksLocationsListName }));
    dispatch(getItems({ listName: myTasksDepartmentsListName }));
    dispatch(getItems({ listName: myTasksRegionsListName }));
    dispatch(getItems({ listName: myTasksKPIListName }));
    dispatch(getItems({ listName: myTasks5WayStatusesListName }));

    // Triggers
    dispatch(getItems({ listName: myTasksTriggersListName }));
    dispatch(getItems({ listName: myTasksDeviationReportTriggersListName }));
  }, []);

  // Фильтры Локации, Департамента, Сектора
  useAsyncEffect(
    async (isMounted) => {
      // if (!isMounted()) return;

      try {
        if (
          submitFilters.locations.length > 0 ||
          submitFilters.departments.length > 0 ||
          submitFilters.sectors.length > 0
        ) {
          setFilterLoading(true);
          setFiveWhyLoading(true);
          setDeviationLoading(true);

          const selected = {
            locations: submitFilters.locations.map((l) => Number(l.id)),
            departments: submitFilters.departments.map((d) => Number(d.id)),
            sectors: submitFilters.sectors.map((s) => Number(s.id)),
            triggers: submitFilters.fiveWhyTriggers.map((t) => Number(t.id)),
            deviationTriggers: submitFilters.deviationTriggers.map((t) => Number(t.id)),
            kpi: submitFilters.kpi.map((k) => Number(k.id)),
          };

          const filters = {
            Location: selected.locations.length > 0 ? selected.locations : undefined,
            Departament: selected.departments.length > 0 ? selected.departments : undefined,
            Region: selected.sectors.length > 0 ? selected.sectors : undefined,
          };

          Object.keys(filters).forEach((key) => {
            if (filters[key as keyof typeof filters] === undefined) {
              delete filters[key as keyof typeof filters];
            }
          });

          const { items: fiveWhyItems } = await getItemsRequest({
            listName: myTasks5WayDependListName,
            filters: filters,
          });

          const { items: deviationItems } = await getItemsRequest({
            listName: myTasksDeviationReportDependListName,
            filters: filters,
          });

          const resultData = {
            locations: new Set<number>(),
            departments: new Set<number>(),
            sectors: new Set<number>(),
            triggers: new Set<number>(),
            deviationTriggers: new Set<number>(),
            kpi: new Set<number>(),
          };

          if (fiveWhyItems.length > 0) {
            const chunks = chunkArray(fiveWhyItems, 25);

            const promises = chunks.map(async (chunk) => {
              const localResultData = {
                locations: new Set<number>(),
                departments: new Set<number>(),
                sectors: new Set<number>(),
                triggers: new Set<number>(),
                kpi: new Set<number>(),
              };

              const modifiedItems = modifiedItemsState(chunk, myTasks5WayDependListName);

              const reducedData = modifiedItems.reduce(
                (acc, item) => {
                  acc.locations.add(item.Location.ID);
                  acc.departments.add(item.Departament.ID);
                  acc.sectors.add(item.Region.ID);
                  acc.triggers.add(item.Trigger.ID);

                  return acc;
                },
                {
                  locations: new Set<number>(),
                  departments: new Set<number>(),
                  sectors: new Set<number>(),
                  triggers: new Set<number>(),
                }
              );

              Array.from(reducedData.locations).forEach((id) => localResultData.locations.add(id));
              Array.from(reducedData.departments).forEach((id) => localResultData.departments.add(id));
              Array.from(reducedData.sectors).forEach((id) => localResultData.sectors.add(id));
              Array.from(reducedData.triggers).forEach((id) => localResultData.triggers.add(id));

              // 5Почему фильтр и отклонение фильтр
              if (reducedData.triggers.size > 0) {
                // КПИ фильтр
                setKpiLoading(true);
                if (localResultData.triggers.size > 0) {
                  const promises = chunkArray(Array.from(localResultData.triggers), 50).map(async (chunk) => {
                    const { items: kpiByTriggerItems } = await getItemsRequest({
                      listName: myTasksKpiByTriggerListName,
                      filters: {
                        Trigger: chunk,
                      },
                      // filtersType: "or",
                    });

                    return kpiByTriggerItems;
                  });

                  const kpiByTriggerItems = await Promise.all(promises);

                  if (kpiByTriggerItems.length > 0) {
                    kpiByTriggerItems.forEach((item) => {
                      item.forEach((item) => localResultData.kpi.add(+item.Kpi.get_lookupId()));
                    });
                  }
                }
              }

              return localResultData;
            });

            const resultResponseData = await Promise.all(promises);

            resultResponseData.forEach((item) => {
              item.locations.forEach((id) => resultData.locations.add(id));
              item.departments.forEach((id) => resultData.departments.add(id));
              item.sectors.forEach((id) => resultData.sectors.add(id));
              item.triggers.forEach((id) => resultData.triggers.add(id));
              item.kpi.forEach((id) => resultData.kpi.add(id));
            });
          }

          if (deviationItems.length > 0) {
            const chunks = chunkArray(deviationItems, 25);

            const promises = chunks.map(async (chunk) => {
              const localResultData = {
                locations: new Set<number>(),
                departments: new Set<number>(),
                sectors: new Set<number>(),
                deviationTriggers: new Set<number>(),
                kpi: new Set<number>(),
              };

              const modifiedItems = modifiedItemsState(chunk, myTasksDeviationReportDependListName);

              const reducedData = modifiedItems.reduce(
                (acc, item) => {
                  acc.locations.add(item.Location.ID);
                  acc.departments.add(item.Departament.ID);
                  acc.sectors.add(item.Region.ID);
                  acc.deviationTriggers.add(item.Trigger.ID);

                  return acc;
                },
                {
                  locations: new Set<number>(),
                  departments: new Set<number>(),
                  sectors: new Set<number>(),
                  triggers: new Set<number>(),
                  deviationTriggers: new Set<number>(),
                }
              );

              Array.from(reducedData.locations).forEach((id) => localResultData.locations.add(id));
              Array.from(reducedData.departments).forEach((id) => localResultData.departments.add(id));
              Array.from(reducedData.sectors).forEach((id) => localResultData.sectors.add(id));
              Array.from(reducedData.deviationTriggers).forEach((id) => localResultData.deviationTriggers.add(id));

              // 5Почему фильтр и отклонение фильтр
              if (reducedData.triggers.size > 0) {
                // КПИ фильтр
                setKpiLoading(true);
                if (localResultData.deviationTriggers.size > 0) {
                  const promises = chunkArray(Array.from(localResultData.deviationTriggers), 50).map(async (chunk) => {
                    const { items: deviationKpiByTriggerItems } = await getItemsRequest({
                      listName: myTasksDeviationReportKpiByTriggerListName,
                      filters: {
                        Trigger: chunk,
                      },
                      // filtersType: "or",
                    });

                    return deviationKpiByTriggerItems;
                  });

                  const deviationKpiByTriggerItems = await Promise.all(promises);

                  if (deviationKpiByTriggerItems.length > 0) {
                    deviationKpiByTriggerItems.forEach((item) => {
                      item.forEach((item) => localResultData.kpi.add(+item.Kpi.get_lookupId()));
                    });
                  }
                }
              }

              return localResultData;
            });

            const resultResponseData = await Promise.all(promises);

            resultResponseData.forEach((item) => {
              item.locations.forEach((id) => resultData.locations.add(id));
              item.departments.forEach((id) => resultData.departments.add(id));
              item.sectors.forEach((id) => resultData.sectors.add(id));
              item.deviationTriggers.forEach((id) => resultData.deviationTriggers.add(id));
              item.kpi.forEach((id) => resultData.kpi.add(id));
            });
          }

          setLocationOptions(
            locationsResponseData.map((loc) => ({
              id: loc.ID,
              title: loc.Title,
              rest: loc,
              disabled:
                firstSelectedFilter === null
                  ? false
                  : firstSelectedFilter !== "locations" && !resultData.locations.has(loc.ID),
            }))
          );

          setDepartmentOptions(
            departmentsResponseData.map((dep) => ({
              id: dep.ID,
              title: dep.Title,
              rest: dep,
              disabled:
                firstSelectedFilter === null
                  ? false
                  : firstSelectedFilter !== "departments" && !resultData.departments.has(dep.ID),
            }))
          );

          setSectorOptions(
            sectorsResponseData.map((sec) => ({
              id: sec.ID,
              title: sec.Title,
              rest: sec,
              disabled:
                firstSelectedFilter === null
                  ? false
                  : firstSelectedFilter !== "sectors" && !resultData.sectors.has(sec.ID),
            }))
          );

          setTriggersOptions(
            triggersResponseData.map((trigger) => ({
              id: trigger.ID,
              title: trigger.Title,
              rest: trigger,
              disabled: !resultData.triggers.has(trigger.ID),
            }))
          );

          setDeviationTriggersOptions(
            deviationTriggersResponseData.map((trigger) => ({
              id: trigger.ID,
              title: trigger.Title,
              rest: trigger,
              disabled: !resultData.deviationTriggers.has(trigger.ID),
            }))
          );

          setKpiOptions(
            kpiResponseData.map((kpi) => ({
              id: kpi.ID,
              title: kpi.Title,
              rest: kpi,
              disabled: !resultData.kpi.has(kpi.ID),
            }))
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setFilterLoading(false);
        setFiveWhyLoading(false);
        setDeviationLoading(false);
        setKpiLoading(false);
      }
    },
    [
      locationsResponseData,
      sectorsResponseData,
      departmentsResponseData,
      submitFilters.locations,
      submitFilters.sectors,
      submitFilters.departments,
      submitFilters.fiveWhyTriggers,
      submitFilters.deviationTriggers,
      submitFilters.kpi,
      firstSelectedFilter,
      locationsStatus,
      sectorsStatus,
      departmentsStatus,
      triggersResponseData,
      deviationTriggersResponseData,
      kpiResponseData,
      triggersStatus,
      deviationTriggersStatus,
      kpiStatus,
    ]
  );

  // 5Почему фильтр
  useAsyncEffect(async () => {
    try {
      if (firstSelectedFilter === "fiveWhyTriggers" && submitFilters.fiveWhyTriggers.length > 0) {
        setFiveWhyLoading(true);
        const fiveWhyTriggersIds = submitFilters.fiveWhyTriggers.map((l) => Number(l.id));

        const { items: fiveWhyDependItems } = await getItemsRequest({
          listName: myTasks5WayDependListName,
          filters: {
            Trigger: fiveWhyTriggersIds,
          },
          // filtersType: "or",
        });

        const resultData = {
          kpi: new Set<number>(),
          locations: new Set<number>(),
          departments: new Set<number>(),
          sectors: new Set<number>(),
        };

        if (fiveWhyDependItems.length > 0) {
          const modifiedFiveWhyDependItems = modifiedItemsState(fiveWhyDependItems, myTasks5WayDependListName);

          const triggerIds = new Set<number>();

          modifiedFiveWhyDependItems.forEach((item) => {
            resultData.locations.add(item.Location.ID);
            resultData.departments.add(item.Departament.ID);
            resultData.sectors.add(item.Region.ID);
            triggerIds.add(item.Trigger.ID);
          });

          setKpiLoading(true);
          const { items: kpiByTriggerItems } = await getItemsRequest({
            listName: myTasksKpiByTriggerListName,
            filters: {
              Trigger: Array.from(triggerIds),
            },
            // filtersType: "or",
          });

          if (kpiByTriggerItems.length > 0) {
            kpiByTriggerItems.map((item) => resultData.kpi.add(+item.Kpi.get_lookupId()));
          }
        }

        setLocationOptions(
          locationsResponseData.map((loc) => ({
            id: loc.ID,
            title: loc.Title,
            rest: loc,
            disabled: !resultData.locations.has(loc.ID),
          }))
        );
        setDepartmentOptions(
          departmentsResponseData.map((dep) => ({
            id: dep.ID,
            title: dep.Title,
            rest: dep,
            disabled: !resultData.departments.has(dep.ID),
          }))
        );
        setSectorOptions(
          sectorsResponseData.map((sec) => ({
            id: sec.ID,
            title: sec.Title,
            rest: sec,
            disabled: !resultData.sectors.has(sec.ID),
          }))
        );

        setKpiOptions(
          kpiResponseData.map((kpi) => ({
            id: kpi.ID,
            title: kpi.Title,
            rest: kpi,
            disabled: !resultData.kpi.has(kpi.ID),
          }))
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFiveWhyLoading(false);
      setKpiLoading(false);
    }
  }, [submitFilters.fiveWhyTriggers, firstSelectedFilter]);

  // Отчеты об отклонении фильтр
  useAsyncEffect(async () => {
    try {
      if (firstSelectedFilter === "deviationTriggers" && submitFilters.deviationTriggers.length > 0) {
        setDeviationLoading(true);
        const deviationTriggersIds = submitFilters.deviationTriggers.map((l) => Number(l.id));

        const { items: deviationItems } = await getItemsRequest({
          listName: myTasksDeviationReportDependListName,
          filters: {
            Trigger: deviationTriggersIds,
          },
          // filtersType: "or",
        });

        const resultData = {
          kpi: new Set<number>(),
          locations: new Set<number>(),
          departments: new Set<number>(),
          sectors: new Set<number>(),
        };

        if (deviationItems.length > 0) {
          const modifiedDeviationItems = modifiedItemsState(deviationItems, myTasksDeviationReportDependListName);

          const triggerIds = new Set<number>();

          modifiedDeviationItems.forEach((item) => {
            resultData.locations.add(item.Location.ID);
            resultData.departments.add(item.Departament.ID);
            resultData.sectors.add(item.Region.ID);
            triggerIds.add(item.Trigger.ID);
          });

          setKpiLoading(true);

          const { items: deviationKpiByTriggerItems } = await getItemsRequest({
            listName: myTasksDeviationReportKpiByTriggerListName,
            filters: {
              Trigger: Array.from(triggerIds),
            },
            // filtersType: "or",
          });

          if (deviationKpiByTriggerItems.length > 0) {
            deviationKpiByTriggerItems.map((item) => resultData.kpi.add(+item.Kpi.get_lookupId()));
          }
        }

        setLocationOptions(
          locationsResponseData.map((loc) => ({
            id: loc.ID,
            title: loc.Title,
            rest: loc,
            disabled: !resultData.locations.has(loc.ID),
          }))
        );
        setDepartmentOptions(
          departmentsResponseData.map((dep) => ({
            id: dep.ID,
            title: dep.Title,
            rest: dep,
            disabled: !resultData.departments.has(dep.ID),
          }))
        );
        setSectorOptions(
          sectorsResponseData.map((sec) => ({
            id: sec.ID,
            title: sec.Title,
            rest: sec,
            disabled: !resultData.sectors.has(sec.ID),
          }))
        );

        setKpiOptions(
          kpiResponseData.map((kpi) => ({
            id: kpi.ID,
            title: kpi.Title,
            rest: kpi,
            disabled: !resultData.kpi.has(kpi.ID),
          }))
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeviationLoading(false);
      setKpiLoading(false);
    }
  }, [submitFilters.deviationTriggers, firstSelectedFilter]);

  // KPI фильтр
  useAsyncEffect(async () => {
    try {
      if (firstSelectedFilter === "kpi" && submitFilters.kpi.length > 0) {
        setKpiLoading(true);
        setFilterLoading(true);
        const kpiIds = submitFilters.kpi.map((kpi) => Number(kpi.id));

        const [deviationKpiByTriggerItems, kpiByTriggerItems] = await Promise.all([
          getItemsRequest({
            listName: myTasksDeviationReportKpiByTriggerListName,
            filters: {
              Kpi: kpiIds,
            },
            // filtersType: "or",
          }),
          getItemsRequest({
            listName: myTasksKpiByTriggerListName,
            filters: {
              Kpi: kpiIds,
            },
            // filtersType: "or",
          }),
        ]);

        const resultData = {
          deviationTriggers: new Set<number>(),
          triggers: new Set<number>(),
          locations: new Set<number>(),
          departments: new Set<number>(),
          sectors: new Set<number>(),
        };

        if (deviationKpiByTriggerItems.items.length > 0) {
          const deviationKpiByTriggerIds = deviationKpiByTriggerItems.items.map((item) => +item.Trigger.get_lookupId());

          deviationKpiByTriggerIds.forEach((id) => resultData.deviationTriggers.add(id));

          setDeviationLoading(true);
          const { items: deviationItems } = await getItemsRequest({
            listName: myTasksDeviationReportDependListName,
            filters: {
              Trigger: deviationKpiByTriggerIds,
            },
            // filtersType: "or",
          });

          if (deviationItems.length > 0) {
            const modifiedDeviationItems = modifiedItemsState(deviationItems, myTasksDeviationReportDependListName);

            const reducedData = modifiedDeviationItems.reduce(
              (acc, item) => {
                acc.locations.add(item.Location.ID);
                acc.departments.add(item.Departament.ID);
                acc.sectors.add(item.Region.ID);

                return acc;
              },
              { locations: new Set<number>(), departments: new Set<number>(), sectors: new Set<number>() }
            );

            Array.from(reducedData.locations).forEach((id) => resultData.locations.add(id));
            Array.from(reducedData.departments).forEach((id) => resultData.departments.add(id));
            Array.from(reducedData.sectors).forEach((id) => resultData.sectors.add(id));
          }
        }

        if (kpiByTriggerItems.items.length > 0) {
          setFiveWhyLoading(true);
          const kpiByTriggerIds = kpiByTriggerItems.items.map((item) => +item.Trigger.get_lookupId());

          kpiByTriggerIds.forEach((id) => resultData.triggers.add(id));

          const { items: fiveWhyItems } = await getItemsRequest({
            listName: myTasks5WayDependListName,
            filters: {
              Trigger: kpiByTriggerIds,
            },
            // filtersType: "or",
          });

          if (fiveWhyItems.length > 0) {
            const modifiedFiveWhyItems = modifiedItemsState(fiveWhyItems, myTasks5WayDependListName);

            const reducedData = modifiedFiveWhyItems.reduce(
              (acc, item) => {
                acc.locations.add(item.Location.ID);
                acc.departments.add(item.Departament.ID);
                acc.sectors.add(item.Region.ID);

                return acc;
              },
              { locations: new Set<number>(), departments: new Set<number>(), sectors: new Set<number>() }
            );

            Array.from(reducedData.locations).forEach((id) => resultData.locations.add(id));
            Array.from(reducedData.departments).forEach((id) => resultData.departments.add(id));
            Array.from(reducedData.sectors).forEach((id) => resultData.sectors.add(id));
          }
        }

        setTriggersOptions(
          triggersResponseData.map((trigger) => ({
            id: trigger.ID,
            title: trigger.Title,
            rest: trigger,
            disabled: !resultData.triggers.has(trigger.ID),
          }))
        );

        setDeviationTriggersOptions(
          deviationTriggersResponseData.map((trigger) => ({
            id: trigger.ID,
            title: trigger.Title,
            rest: trigger,
            disabled: !resultData.deviationTriggers.has(trigger.ID),
          }))
        );
        setLocationOptions(
          locationsResponseData.map((loc) => ({
            id: loc.ID,
            title: loc.Title,
            rest: loc,
            disabled: !resultData.locations.has(loc.ID),
          }))
        );
        setDepartmentOptions(
          departmentsResponseData.map((dep) => ({
            id: dep.ID,
            title: dep.Title,
            rest: dep,
            disabled: !resultData.departments.has(dep.ID),
          }))
        );
        setSectorOptions(
          sectorsResponseData.map((sec) => ({
            id: sec.ID,
            title: sec.Title,
            rest: sec,
            disabled: !resultData.sectors.has(sec.ID),
          }))
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setKpiLoading(false);
      setDeviationLoading(false);
      setFiveWhyLoading(false);
      setFilterLoading(false);
    }
  }, [submitFilters.kpi, firstSelectedFilter]);

  // Фильтры
  useEffect(() => {
    if (triggersStatus === "SUCCESS") {
      setTriggersOptions(triggersResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (deviationTriggersStatus === "SUCCESS") {
      setDeviationTriggersOptions(deviationTriggersResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (locationsStatus === "SUCCESS") {
      setLocationOptions(locationsResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (departmentsStatus === "SUCCESS") {
      setDepartmentOptions(departmentsResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (sectorsStatus === "SUCCESS") {
      setSectorOptions(sectorsResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (kpiStatus === "SUCCESS") {
      setKpiOptions(kpiResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }
  }, [
    triggersResponseData,
    deviationTriggersResponseData,
    triggersStatus,
    deviationTriggersStatus,
    locationsResponseData,
    departmentsResponseData,
    sectorsResponseData,
    locationsStatus,
    departmentsStatus,
    sectorsStatus,
    kpiResponseData,
    kpiStatus,
  ]);

  return (
    <FlexCol className="overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-full custom-selects">
      {isFiltersEmpty && (
        <FlexRow className="flex-nowrap gap-x-2 items-center p-5 border-b cursor-pointer group" onClick={clearAll}>
          <FlexCol className="group-hover:underline">Сбросить все фильтры</FlexCol>
          <FlexCol>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_381_6476)">
                <path
                  d="M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z"
                  fill="#637381"
                />
              </g>
              <defs>
                <clipPath id="clip0_381_6476">
                  <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
                </clipPath>
              </defs>
            </svg>
          </FlexCol>
        </FlexRow>
      )}

      <TGDisclosure title="Дата создания" count={createDate?.startDate ? 1 : 0}>
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <h4 className={"font-normal"}>От и До</h4>
          <TGDatePicker
            value={createDate}
            onChange={(value) => checkListDateItem("createDate", value)}
            popoverDirection="down"
          />
          <SubResetFilter resetFilter={() => reset("createDate", { start: null, end: null })} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure
        title="Статус 5Почему"
        count={fiveWhyStatuses.length}
        disabled={fiveWayStatusesStatus === "LOADING"}
      >
        <TGCheckList
          list={fiveWayStatusesResponseData.map((status) => ({ id: status.ID, title: status.Title, rest: status }))}
          items={fiveWhyStatuses}
          onChecked={(value) => checkListItem("fiveWhyStatuses", value)}
        />
        <SubResetFilter resetFilter={() => reset("fiveWhyStatuses", [])} />
      </TGDisclosure>
      <TGDisclosure
        title="Статус Отчетов об отклонении"
        count={deviationStatuses.length}
        disabled={fiveWayStatusesStatus === "LOADING"}
      >
        <TGCheckList
          list={fiveWayStatusesResponseData.map((status) => ({ id: status.ID, title: status.Title, rest: status }))}
          items={deviationStatuses}
          onChecked={(value) => checkListItem("deviationStatuses", value)}
        />
        <SubResetFilter resetFilter={() => reset("deviationStatuses", [])} />
      </TGDisclosure>
      <TGDisclosure title="Локация" count={locations.length} disabled={filterLoading || locationsStatus === "LOADING"}>
        <TGListWithSearch
          listData={locationOptions}
          items={locations}
          checkedItems={(value) => checkListItem("locations", value)}
        />
        <SubResetFilter resetFilter={() => reset("locations", [])} />
      </TGDisclosure>
      <TGDisclosure
        title="Отдел"
        count={departments.length}
        disabled={filterLoading || departmentsStatus === "LOADING"}
      >
        <TGListWithSearch
          listData={departmentOptions}
          items={departments}
          checkedItems={(value) => checkListItem("departments", value)}
        />
        <SubResetFilter resetFilter={() => reset("departments", [])} />
      </TGDisclosure>
      <TGDisclosure title="Участок" count={sectors.length} disabled={filterLoading || sectorsStatus === "LOADING"}>
        <TGListWithSearch
          listData={sectorOptions}
          items={sectors}
          checkedItems={(value) => checkListItem("sectors", value)}
        />
        <SubResetFilter resetFilter={() => reset("sectors", [])} />
      </TGDisclosure>
      <TGDisclosure
        title="Триггер 5Почему"
        count={fiveWhyTriggers.length}
        disabled={fiveWhyLoading || triggersStatus === "LOADING"}
      >
        <TGListWithSearch
          listData={triggersOptions}
          items={fiveWhyTriggers}
          checkedItems={(value) => checkListItem("fiveWhyTriggers", value)}
        />
        <SubResetFilter resetFilter={() => reset("fiveWhyTriggers", [])} />
      </TGDisclosure>
      <TGDisclosure
        title="Триггер Отчетов об отклонении"
        count={deviationTriggers.length}
        disabled={deviationLoading || deviationTriggersStatus === "LOADING"}
      >
        <TGListWithSearch
          listData={deviationTriggersOptions}
          items={deviationTriggers}
          checkedItems={(value) => checkListItem("deviationTriggers", value)}
        />
        <SubResetFilter resetFilter={() => reset("deviationTriggers", [])} />
      </TGDisclosure>
      <TGDisclosure title="KPI" count={kpi.length} disabled={kpiLoading || kpiStatus === "LOADING"}>
        <TGListWithSearch listData={kpiOptions} items={kpi} checkedItems={(value) => checkListItem("kpi", value)} />
        <SubResetFilter resetFilter={() => reset("kpi", [])} />
      </TGDisclosure>
      <TGDisclosure title="Автор" count={author.length}>
        <FilterUser
          selected={author}
          checkEmployees={(value) => checkListUserItem("author", value)}
          reset={() => reset("author", [])}
        />
      </TGDisclosure>
      {/* <TGDisclosure title="Ответственный 5Почему" count={fiveWhyResponsible.length}>
        <FilterUser
          selected={fiveWhyResponsible}
          checkEmployees={(value) => checkListUserItem("fiveWhyResponsible", value)}
          reset={() => reset("fiveWhyResponsible", [])}
        />
      </TGDisclosure> */}
      <TGDisclosure title="Участники Отчетов об отклонении" count={deviationParticipants.length}>
        <FilterUser
          selected={deviationParticipants}
          checkEmployees={(value) => checkListUserItem("deviationParticipants", value)}
          reset={() => reset("deviationParticipants", [])}
        />
      </TGDisclosure>
      <TGDisclosure title="Повторяющаяся проблема">
        <TGRadioList
          item={repeatProblem}
          list={[
            { id: 1, title: "Да", type: "true", value: true },
            { id: 2, title: "Нет", type: "false", value: false },
          ]}
          onChecked={(item) => radioListItem("repeatProblem", item)}
        />
        <SubResetFilter
          resetFilter={() => reset("repeatProblem", { id: 0, title: "Не выбрано", type: "null", value: null })}
        />
      </TGDisclosure>
      <TGDisclosure title="План реагирования выполнен">
        <TGRadioList
          item={isReactPlanAccept}
          list={[
            { id: 1, title: "Да", type: "true", value: true },
            { id: 2, title: "Нет", type: "false", value: false },
            { id: 3, title: "План реагирования отсутствует", type: "noResponse", value: "noResponse" },
          ]}
          onChecked={(item) => radioListItem("isReactPlanAccept", item)}
        />
        <SubResetFilter
          resetFilter={() => reset("isReactPlanAccept", { id: 0, title: "Не выбрано", type: "null", value: null })}
        />
      </TGDisclosure>
      <TGDisclosure title="Корневая причина выявлена">
        <TGRadioList
          item={isRootProblemIdentify}
          list={[
            { id: 1, title: "Да", type: "true", value: true },
            { id: 2, title: "Нет", type: "false", value: false },
          ]}
          onChecked={(item) => radioListItem("isRootProblemIdentify", item)}
        />
        <SubResetFilter
          resetFilter={() => reset("isRootProblemIdentify", { id: 0, title: "Не выбрано", type: "null", value: null })}
        />
      </TGDisclosure>
      <TGDisclosure title="Действия запланированы">
        <TGRadioList
          item={actionsPlanned}
          list={[
            { id: 1, title: "Да", type: "true", value: true },
            { id: 2, title: "Нет", type: "false", value: false },
          ]}
          onChecked={(item) => radioListItem("actionsPlanned", item)}
        />
        <SubResetFilter
          resetFilter={() => reset("actionsPlanned", { id: 0, title: "Не выбрано", type: "null", value: null })}
        />
      </TGDisclosure>
    </FlexCol>
  );
}

export default MatrixReportFilters;
